import React, { useState } from "react";
import { Box, IconButton, Stack } from "@mui/material";
import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid-premium";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import { GeneralHealthInfoData, GeneralHealthInfoCategory } from "./data";
import { useTranslate } from "src/locales";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";

//YATAY;452641;18042025
const PageThreeColumns: React.FC = () => {
  const { t } = useTranslate();
  const [categories, setCategories] = useState<GeneralHealthInfoCategory[]>(
    GeneralHealthInfoData
  );

  const toggleCategory = (id: number) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === id ? { ...cat, expanded: !cat.expanded } : cat
      )
    );
  };

  const columns: GridColDef[] = [
    {
      field: "select",
      headerName: "",
      width: 60, // Sabit genişlik
      sortable: false,
      renderCell: (params) => {
        if (params.row.isHeader) return null;
        return <ByzMuiCheckBox />;
      },
    },
    {
      field: "name",
      headerName: t("adult-patient-assessment.name-two"),
      flex: 1,
      minWidth: 300,
      maxWidth: 300,
      renderCell: (params) => {
        if (params.row.isHeader) {
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size="small"
                onClick={() => {
                  const catId = Number(params.row.id.replace("header-", ""));
                  toggleCategory(catId);
                }}
              >
                {categories.find((cat) => `header-${cat.id}` === params.row.id)
                  ?.expanded ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
              <ByzMuiTypography sx={{ fontWeight: "bold" }}>
                {params.row.name}
              </ByzMuiTypography>
            </Stack>
          );
        }
        return params.value || "";
      },
    },
    {
      field: "note",
      headerName: t("adult-patient-assessment.note"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "disease_type",
      headerName: t("adult-patient-assessment.disease-type"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "special_notes",
      headerName: t("adult-patient-assessment.special-notes"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "type_if_substance",
      headerName: t("adult-patient-assessment.type-if-substance"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "quantity_per_day",
      headerName: t("adult-patient-assessment.quantity-per-day"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "duration_in_years",
      headerName: t("adult-patient-assessment.duration-in-years"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "quit_date",
      headerName: t("adult-patient-assessment.quit-date"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "disease_with_date",
      headerName: t("adult-patient-assessment.disease-with-date"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "surgery_institution",
      headerName: t("adult-patient-assessment.surgery-institution"),
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (params.row.isHeader ? null : params.value || ""),
    },
    {
      field: "edit",
      headerName: t("adult-patient-assessment.edit"),
      flex: 1,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => {
        if (params.row.isHeader) return null;
        return <ByzGrdEditButton />;
      },
    },
  ];

  // Column grouping modeli: her grup eşit alan alacak şekilde flex ile ayarlandı.
  const columnGroupingModel: GridColumnGroupingModel = [
    {
      groupId: "secim",
      headerName: "",
      children: [{ field: "select" }],
    },
    {
      groupId: "Genaral",
      headerName: t("adult-patient-assessment.general"),
      children: [
        { field: "name" },
        { field: "note" },
        { field: "disease_type" },
        { field: "special_notes" },
      ],
    },
    {
      groupId: "Habits",
      headerName: t("adult-patient-assessment.habits"),
      children: [
        { field: "type_if_substance" },
        { field: "quantity_per_day" },
        { field: "duration_in_years" },
        { field: "quit_date" },
      ],
    },
    {
      groupId: "Other",
      headerName: t("adult-patient-assessment.other"),
      children: [
        { field: "disease_with_date" },
        { field: "surgery_institution" },
        { field: "edit" },
      ],
    },
  ];

  // rows oluşturma: her kategori için header ve alt veriler (kategori açık ise)
  const rows = categories.flatMap((category) => {
    const headerRow = {
      id: `header-${category.id}`,
      isHeader: true,
      name: category.category,
    };

    if (!category.expanded) {
      return [headerRow];
    }

    const generalRows = category.generalItem.map((item) => ({
      id: `general-${category.id}-${item.id}`,
      isHeader: false,
      name: item.name,
      note: item.note,
      disease_type: item.disease_type,
      special_notes: item.special_notes,
      type_if_substance: "",
      quantity_per_day: "",
      duration_in_years: "",
      quit_date: "",
      disease_with_date: "",
      surgery_institution: "",
      edit: "",
    }));

    const habitsRows = category.habitsItem.map((item) => ({
      id: `habits-${category.id}-${item.id}`,
      isHeader: false,
      name: "",
      note: "",
      disease_type: "",
      special_notes: "",
      type_if_substance: item.type_if_substance,
      quantity_per_day: item.quantity_per_day,
      duration_in_years: item.duration_in_years,
      quit_date: item.quit_date,
      disease_with_date: "",
      surgery_institution: "",
      edit: "",
    }));

    const otherRows = category.otherItem.map((item) => ({
      id: `other-${category.id}-${item.id}`,
      isHeader: false,
      name: "",
      note: "",
      disease_type: "",
      special_notes: "",
      type_if_substance: "",
      quantity_per_day: "",
      duration_in_years: "",
      quit_date: "",
      disease_with_date: item.disease_with_date,
      surgery_institution: item.surgery_institution,
      edit: item.edit,
    }));

    return [headerRow, ...generalRows, ...habitsRows, ...otherRows];
  });

  return (
    <Box
      sx={{ padding: 2, width: "100%", maxHeight: "90vh", overflow: "auto" }}
    >
      <ByzMuiDataGrid
        columns={columns}
        rows={rows}
        pageSize={10}
        getRowId={(row) => row.id}
        columnGroupingModel={columnGroupingModel}
      />
    </Box>
  );
};

export default PageThreeColumns;
